import React, { useState, useRef } from 'react';
import {
  MdUpload, MdClose, MdCheckCircle, MdError,
  MdWarning, MdRefresh, MdFilePresent, MdTableChart,
  MdExpandMore, MdExpandLess
} from 'react-icons/md';
import { FaFileExcel } from 'react-icons/fa';
import './BulkUpload.css';

/**
 * Excel Bulk Upload Component for Job Management
 * Handles Excel files with "JR Sample" and "Pipeline Sample" sheets
 *
 * @param {Object} props
 * @param {string} props.title - Modal title
 * @param {Function} props.onUpload - Async function to handle file upload
 * @param {Function} props.onClose - Function to close the modal
 */
function ExcelBulkUpload({
  title = 'Bulk Upload from Excel',
  onUpload,
  onClose
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [step, setStep] = useState('upload'); // 'upload', 'confirm', 'result'
  const [error, setError] = useState(null);
  const [showAllErrors, setShowAllErrors] = useState(false);

  // Helper to categorize errors by type (JR vs Candidate)
  const categorizeErrors = (errors) => {
    if (!errors || !Array.isArray(errors)) return { jr: [], candidate: [], other: [] };

    const categorized = { jr: [], candidate: [], other: [] };
    errors.forEach(err => {
      if (err.startsWith('JR Row') || err.includes('JR Row')) {
        categorized.jr.push(err);
      } else if (err.startsWith('Candidate Row') || err.includes('Candidate Row')) {
        categorized.candidate.push(err);
      } else {
        categorized.other.push(err);
      }
    });
    return categorized;
  };

  // Expected sheets and columns info
  const templateInfo = {
    sheets: [
      {
        name: 'JR Sample',
        description: 'Job Requisitions data',
        columns: [
          'S.No', 'Vertical', 'Req id', 'Approved in WD', 'Req Approval Date', 'SLA',
          'Evergreen', 'Ageing', 'JO Closure Date', 'TAT', 'Location', 'Revised Location',
          'Exp/Fresher', 'HR Lead', 'Hiring Manager', 'BU Head', 'Hiring Type',
          'Cost Center', 'Product Line', 'Project (IND)', 'Local Cost Center (IND)',
          'Designation', 'Specialty', 'H.B./P.B', 'EMR', 'Req status',
          'Original Demand', 'Joined (till date)', 'Current Open', 'Offered/Yet to offer'
        ]
      },
      {
        name: 'Pipeline Sample',
        description: 'Candidates pipeline data',
        columns: [
          'S.No', 'HIM/RCM/Support/IT', 'DOI', 'DOJ', 'Candidate ID', 'Candidate Name',
          'Designation', 'Fresher/Experience', 'Process', 'Speciality', 'BU Lead', 'BU Head',
          'Project', 'Work Location', 'Contact No', 'Email', 'Address', 'Recruiter',
          'Source', 'Sub Source', 'Assessment Score', 'V&A interviewer', 'Ops Interviewer',
          'Offer Status', 'HB / PB', 'Host System', 'SPOC', 'UAN Check', 'UAN - Date',
          'Last Company/Current Company', 'Re-hire', 'WFH/WFO', 'Evergreen Requisition',
          'Actual Requisition'
        ]
      }
    ]
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Check file extension
    const validExtensions = ['.xlsx', '.xls'];
    const fileExt = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExt)) {
      setError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setUploadResult(null);
    setStep('confirm');
  };

  // Handle upload
  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setError(null);

    try {
      const result = await onUpload(file);
      setUploadResult({
        success: true,
        message: result?.message || 'Upload completed successfully',
        data: result?.data || {}
      });
      setStep('result');
    } catch (err) {
      setUploadResult({
        success: false,
        message: err.response?.data?.error || err.message || 'Upload failed'
      });
      setStep('result');
    } finally {
      setIsUploading(false);
    }
  };

  // Reset to initial state
  const handleReset = () => {
    setFile(null);
    setError(null);
    setUploadResult(null);
    setStep('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content bulk-upload-modal excel-upload-modal">
        <div className="modal-header">
          <h3><FaFileExcel style={{ color: '#217346', marginRight: '8px' }} />{title}</h3>
          <button className="modal-close" onClick={onClose}>
            <MdClose />
          </button>
        </div>

        <div className="bulk-upload-content">
          {step === 'upload' && (
            <>
              <div className="upload-instructions">
                <h4>Upload Excel File</h4>
                <p>Upload an Excel file (.xlsx or .xls) containing Job Requisitions and/or Candidates data.</p>

                <div className="excel-sheets-info">
                  <h5><MdTableChart /> Expected Sheets:</h5>
                  {templateInfo.sheets.map((sheet, idx) => (
                    <div key={idx} className="sheet-info">
                      <div className="sheet-name">
                        <strong>{sheet.name}</strong> - {sheet.description}
                      </div>
                      <div className="sheet-columns">
                        <span className="columns-label">Columns ({sheet.columns.length}):</span>
                        <div className="columns-list">
                          {sheet.columns.slice(0, 8).map((col, i) => (
                            <span key={i} className="column-tag">{col}</span>
                          ))}
                          {sheet.columns.length > 8 && (
                            <span className="column-more">+{sheet.columns.length - 8} more</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="upload-notes">
                  <h5>Notes:</h5>
                  <ul>
                    <li>Both sheets are optional - upload data for JRs only, Candidates only, or both</li>
                    <li>The system will automatically create missing master data (Verticals, Designations, etc.)</li>
                    <li>Existing records will be updated based on Req ID or Candidate ID</li>
                    <li>First row of each sheet should contain column headers</li>
                  </ul>
                </div>
              </div>

              <div
                className="drop-zone excel-drop-zone"
                onClick={() => fileInputRef.current?.click()}
              >
                <FaFileExcel className="drop-icon excel-icon" />
                <p>Click to select or drag and drop an Excel file</p>
                <span className="file-types">.xlsx, .xls files supported</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </div>

              {error && (
                <div className="error-list">
                  <h5><MdError /> Error</h5>
                  <p>{error}</p>
                </div>
              )}
            </>
          )}

          {step === 'confirm' && (
            <>
              <div className="preview-header">
                <div className="file-info">
                  <FaFileExcel style={{ color: '#217346', fontSize: '24px' }} />
                  <span className="file-name">{file?.name}</span>
                  <span className="file-size">
                    ({(file?.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <button className="btn-reset" onClick={handleReset}>
                  <MdRefresh /> Choose Different File
                </button>
              </div>

              <div className="confirm-section">
                <div className="confirm-icon">
                  <MdWarning style={{ color: '#f59e0b', fontSize: '48px' }} />
                </div>
                <h4>Ready to Upload</h4>
                <p>
                  This will process the Excel file and import data into the system.
                  The following actions will be performed:
                </p>
                <ul className="action-list">
                  <li>Job Requisitions from "JR Sample" sheet will be created/updated</li>
                  <li>Candidates from "Pipeline Sample" sheet will be created/updated</li>
                  <li>Missing master data (Verticals, Designations, etc.) will be auto-created</li>
                  <li>Candidates will be linked to Job Requisitions by "Actual Requisition" column</li>
                </ul>
              </div>

              <div className="upload-actions">
                <button className="btn-cancel" onClick={handleReset}>
                  Cancel
                </button>
                <button
                  className="btn-upload excel-btn"
                  onClick={handleUpload}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className="spinner"></span>
                      Processing Excel...
                    </>
                  ) : (
                    <>
                      <MdUpload /> Upload & Process
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          {step === 'result' && (
            <div className="result-container">
              {uploadResult?.success ? (
                <>
                  <div className="result-icon success">
                    <MdCheckCircle />
                  </div>
                  <h4>Upload Successful!</h4>
                  <p>{uploadResult.message}</p>

                  {uploadResult.data && (
                    <div className="result-details">
                      {uploadResult.data.requisitionsCreated !== undefined && (
                        <div className="result-stat">
                          <span className="stat-label">Job Requisitions Created:</span>
                          <span className="stat-value">{uploadResult.data.requisitionsCreated || 0}</span>
                        </div>
                      )}
                      {uploadResult.data.requisitionsUpdated !== undefined && (
                        <div className="result-stat">
                          <span className="stat-label">Job Requisitions Updated:</span>
                          <span className="stat-value">{uploadResult.data.requisitionsUpdated || 0}</span>
                        </div>
                      )}
                      {uploadResult.data.candidatesCreated !== undefined && (
                        <div className="result-stat">
                          <span className="stat-label">Candidates Created:</span>
                          <span className="stat-value">{uploadResult.data.candidatesCreated || 0}</span>
                        </div>
                      )}
                      {uploadResult.data.candidatesUpdated !== undefined && (
                        <div className="result-stat">
                          <span className="stat-label">Candidates Updated:</span>
                          <span className="stat-value">{uploadResult.data.candidatesUpdated || 0}</span>
                        </div>
                      )}
                      {uploadResult.data.errors && uploadResult.data.errors.length > 0 && (
                        <div className="result-errors-section">
                          <div
                            className="errors-header"
                            onClick={() => setShowAllErrors(!showAllErrors)}
                          >
                            <h5>
                              <MdError style={{ color: '#dc2626' }} />
                              <span>Failed Rows ({uploadResult.data.errors.length})</span>
                            </h5>
                            <button className="expand-btn">
                              {showAllErrors ? <MdExpandLess /> : <MdExpandMore />}
                              {showAllErrors ? 'Collapse' : 'View All'}
                            </button>
                          </div>

                          {(() => {
                            const categorized = categorizeErrors(uploadResult.data.errors);
                            const displayErrors = showAllErrors
                              ? uploadResult.data.errors
                              : uploadResult.data.errors.slice(0, 5);

                            return (
                              <div className={`errors-content ${showAllErrors ? 'expanded' : ''}`}>
                                {/* Show categorized summary */}
                                <div className="errors-summary">
                                  {categorized.jr.length > 0 && (
                                    <span className="error-category jr">
                                      JR Errors: {categorized.jr.length}
                                    </span>
                                  )}
                                  {categorized.candidate.length > 0 && (
                                    <span className="error-category candidate">
                                      Candidate Errors: {categorized.candidate.length}
                                    </span>
                                  )}
                                  {categorized.other.length > 0 && (
                                    <span className="error-category other">
                                      Other: {categorized.other.length}
                                    </span>
                                  )}
                                </div>

                                {/* Error list */}
                                <div className="errors-list-container">
                                  <ul className="errors-list">
                                    {displayErrors.map((err, idx) => {
                                      // Parse the error to highlight the row info
                                      const match = err.match(/^((?:JR|Candidate) Row \d+.*?) - (.+)$/);
                                      if (match) {
                                        return (
                                          <li key={idx} className="error-item">
                                            <span className="error-row-id">{match[1]}</span>
                                            <span className="error-message">{match[2]}</span>
                                          </li>
                                        );
                                      }
                                      return (
                                        <li key={idx} className="error-item">
                                          <span className="error-message">{err}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                  {!showAllErrors && uploadResult.data.errors.length > 5 && (
                                    <div className="errors-more">
                                      Click "View All" to see {uploadResult.data.errors.length - 5} more errors
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="result-icon error">
                    <MdError />
                  </div>
                  <h4>Upload Failed</h4>
                  <p>{uploadResult?.message}</p>
                </>
              )}

              <div className="result-actions">
                <button className="btn-reset" onClick={handleReset}>
                  Upload Another File
                </button>
                <button className="btn-done" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExcelBulkUpload;
